import { useState, useRef, useCallback } from "react";
import { sectionDataObject } from "@/lib/stores/objectsStore";
import {
  getCachedStringsItems,
  updateTranslationLine,
  createTranslationLine,
} from "@/lib/stores/stringsStore";
import { FormFieldObject } from "@/lib/stores/objectsStore";
import { cleanupInvalidGlossaryReferences } from "./objectFormUtils";
import { useDisplayLanguageOptional, resolveStringDisplayText } from "@/contexts/DisplayLanguageContext";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";

/**
 * Hook for managing glossary form state
 */
export const useGlossaryFormState = (
  sections: sectionDataObject[],
  onInputChange: (fieldId: string, value: any) => void,
  currentGameId?: string,
  availableGlossaryItems?: any[]
) => {
  const [localFormData, setLocalFormData] = useState<sectionDataObject[]>(sections);
  const prevSectionsRef = useRef<sectionDataObject[]>(sections);
  const displayLanguageCtx = useDisplayLanguageOptional();
  const displayLanguage = displayLanguageCtx?.displayLanguage ?? null;
  const { t } = usePlatformTranslation();
  const noTranslationLabel = t('platform.TMS.no_translation');

  /**
   * Get string content for translatable fields.
   * When a display language is selected, returns the translation for that language,
   * or a translated "no translation" label if none exists.
   */
  const getStringContent = useCallback((field: FormFieldObject): string | null => {
    if (!field.translation || !field.string_id || !currentGameId) {
      return null;
    }
    
    const strings = getCachedStringsItems(currentGameId);
    const stringItem = strings.find(s => s.id === field.string_id);
    if (!stringItem) return null;

    const resolved = resolveStringDisplayText(stringItem, displayLanguage, noTranslationLabel);
    return resolved.text;
  }, [currentGameId, displayLanguage, noTranslationLabel]);

  /**
   * Get the current string content for a field (for both view and edit modes)
   */
  const getCurrentFieldContent = useCallback((field: FormFieldObject): string => {
    // If field is translatable and has a string_id, get text from strings store
    if (field.translation && field.string_id) {
      const stringContent = getStringContent(field);
      if (stringContent) {
        return stringContent;
      }
    }
    
    // Fallback to field value if no string content available
    return field.value && typeof field.value === 'string' ? field.value : '';
  }, [getStringContent]);

  /**
   * Clean up invalid glossary item references in form data
   */
  const cleanupInvalidReferences = useCallback(() => {
    if (!availableGlossaryItems) return;
    
    setLocalFormData((prev) => {
      let hasChanges = false;
      const cleanedData = prev.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          if (field.type === 'glossaryItem' && field.value) {
            const cleanedValue = cleanupInvalidGlossaryReferences(field.value, availableGlossaryItems);
            if (cleanedValue !== field.value) {
              hasChanges = true;
              // Notify parent of the cleanup
              onInputChange(field.id, cleanedValue);
              return { ...field, value: cleanedValue };
            }
          }
          return field;
        }),
      }));
      
      return hasChanges ? cleanedData : prev;
    });
  }, [availableGlossaryItems, onInputChange]);

  /**
   * Update field value in sections
   */
  const updateFieldValue = useCallback((fieldId: string, value: any) => {
    setLocalFormData((prev) => {
      return prev.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          field.id === fieldId ? { ...field, value } : field
        ),
      }));
    });
  }, []);

  /**
   * Update a translatable field and its associated string
   */
  const updateTranslatableField = useCallback(async (fieldId: string, newValue: string) => {
    // Find the field to check if it's translatable
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    
    // For translatable fields, we only update the local value and notify parent
    // Strings will be updated when the glossary item is saved/updated
    if (field && field.translation) {
      // Update the local field value for immediate UI feedback
      updateFieldValue(fieldId, newValue);
      
      // Notify parent of the change
      onInputChange(fieldId, newValue);
    } else {
      // Not a translatable field, just update normally
      updateFieldValue(fieldId, newValue);
      onInputChange(fieldId, newValue);
    }
  }, [localFormData, updateFieldValue, onInputChange]);

  /**
   * Get field value by ID
   */
  const getFieldValue = useCallback((fieldId: string): any => {
    for (const section of localFormData) {
      const field = section.fields.find((f) => f.id === fieldId);
      if (field) {
        return field.value;
      }
    }
    return undefined;
  }, [localFormData]);

  /**
   * Handle immediate local updates - ONLY update local state, don't notify parent
   * Exception: Boolean fields notify parent immediately since checkboxes don't fire blur events
   */
  const handleLocalInputChange = useCallback((fieldId: string, value: any) => {
    // For URL fields, ensure we're storing the urlValue object
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    if (field && field.type === "url") {
      // Ensure we're storing a proper urlValue object
      const urlValueObj = typeof value === "object" && value.url !== undefined 
        ? value 
        : { url: "", text: "" };
      updateFieldValue(fieldId, urlValueObj);
      // Don't call onInputChange here - only update local state
    } else if (field && field.type === "boolean") {
      // For boolean fields, notify parent immediately since checkboxes don't fire blur events
      updateFieldValue(fieldId, value);
      onInputChange(fieldId, value);
    } else if (field && field.translation && field.string_id && typeof value === 'string') {
      // For translatable fields, only update local state, don't notify parent yet
      updateFieldValue(fieldId, value);
      // Don't call updateTranslatableField here - wait for blur
    } else {
      // For other fields, only update local state
      updateFieldValue(fieldId, value);
      // Don't call onInputChange here - only update local state
    }
  }, [localFormData, updateFieldValue, onInputChange]);

  /**
   * Ref to track translation IDs created during this session (per string_id).
   * Prevents duplicate creates when the user edits multiple times before the store updates.
   */
  const createdTranslationIdsRef = useRef<Record<string, string>>({});

  /**
   * Handle field blur - send update to parent ONLY when focus is lost.
   * When in translation mode (displayLanguage is set), saves to TMS translation
   * instead of updating the source text.
   */
  const handleFieldBlur = useCallback((fieldId: string, value: any) => {
    // Ensure local state is updated with the final value
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    if (field && field.type === "url") {
      // For URL fields, notify parent on blur
      const urlValueObj = typeof value === "object" && value.url !== undefined 
        ? value 
        : { url: "", text: "" };
      updateFieldValue(fieldId, urlValueObj);
      onInputChange(fieldId, urlValueObj); // Notify parent on blur
    } else if (field && field.translation && field.string_id && typeof value === 'string') {
      if (displayLanguage && currentGameId) {
        // Translation mode: save to TMS translation line, NOT to source field.value
        const strings = getCachedStringsItems(currentGameId);
        const stringItem = strings.find(s => s.id === field.string_id);
        const existingTranslation = stringItem?.translations?.find(
          (t) => t.language_code === displayLanguage
        );
        const cachedCreatedId = createdTranslationIdsRef.current[`${field.string_id}_${displayLanguage}`];
        const translationId = existingTranslation?.id || cachedCreatedId;

        if (translationId) {
          updateTranslationLine(translationId, {
            language_code: displayLanguage,
            translated_text: value,
            approved: false,
          }).catch(err => console.error('[objectFormState] Translation update failed:', err));
        } else {
          createTranslationLine({
            dialogue_line_id: field.string_id,
            language_code: displayLanguage,
            translated_text: value,
            approved: false,
          }).then(result => {
            if (result?.id) {
              createdTranslationIdsRef.current[`${field.string_id}_${displayLanguage}`] = result.id;
            }
          }).catch(err => console.error('[objectFormState] Translation create failed:', err));
        }

        // Update local state so the UI reflects what the user typed
        updateFieldValue(fieldId, value);
        // Do NOT notify parent (onInputChange) — we don't want to mark the form dirty
        // with translation text that would overwrite source_text on save
      } else {
        // Source mode: update strings store and notify parent on blur
        updateTranslatableField(fieldId, value);
      }
    } else {
      // For other fields, notify parent on blur
      updateFieldValue(fieldId, value);
      onInputChange(fieldId, value); // Notify parent on blur
    }
  }, [localFormData, updateFieldValue, onInputChange, updateTranslatableField, displayLanguage, currentGameId]);

  /**
   * Handle multiple field values - update local state and notify parent
   * Unlike single fields that use blur to commit, multiple fields need immediate notification
   * because they manage their own array of values
   */
  const handleMultipleFieldChange = useCallback((
    fieldId: string,
    index: number,
    value: any
  ) => {
    const currentValue = getFieldValue(fieldId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter((v) => v !== undefined);
    const newValues = [...currentValues];
    
    // For URL fields, ensure we're storing urlValue objects
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    if (field && field.type === "url") {
      const urlValueObj = typeof value === "object" && value.url !== undefined 
        ? value 
        : { url: "", text: "" };
      newValues[index] = urlValueObj;
    } else if (field && field.translation) {
      // For translatable fields, maintain TranslatableValue structure
      const currentItem = currentValues[index];
      const existingStringId = typeof currentItem === 'object' && currentItem !== null && 'string_id' in currentItem
        ? currentItem.string_id
        : undefined;
      
      // Store as TranslatableValue object
      newValues[index] = {
        text: typeof value === 'string' ? value : (value?.text || ''),
        string_id: existingStringId
      };
    } else {
      newValues[index] = value;
    }
    
    updateFieldValue(fieldId, newValues);
    // Notify parent immediately so "Save Changes" button gets enabled
    onInputChange(fieldId, newValues);
  }, [getFieldValue, localFormData, updateFieldValue, onInputChange]);

  /**
   * Add a new field instance - update local state and notify parent
   */
  const addFieldInstance = useCallback((fieldId: string) => {
    const currentValue = getFieldValue(fieldId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter((v) => v !== undefined);
    
    // For URL fields, add empty urlValue object
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    let newValue: any;
    
    if (field && field.type === "url") {
      newValue = { url: "", text: "" };
    } else if (field && field.translation) {
      // For translatable fields, add empty TranslatableValue object
      newValue = { text: "", string_id: undefined };
    } else {
      newValue = "";
    }
    
    const newValues = [...currentValues, newValue];
    updateFieldValue(fieldId, newValues);
    // Notify parent so "Save Changes" button gets enabled
    onInputChange(fieldId, newValues);
  }, [getFieldValue, localFormData, updateFieldValue, onInputChange]);

  /**
   * Remove a field instance - update local state and notify parent
   */
  const removeFieldInstance = useCallback((fieldId: string, index: number) => {
    const currentValue = getFieldValue(fieldId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter((v) => v !== undefined);
    const newValues = currentValues.filter((_, i) => i !== index);
    
    // For URL fields, ensure we maintain urlValue object structure
    const field = localFormData.flatMap(s => s.fields).find(f => f.id === fieldId);
    let finalValue: any;
    
    if (field && field.type === "url") {
      finalValue = newValues.length > 0 ? newValues : [{ url: "", text: "" }];
    } else if (field && field.translation) {
      // For translatable fields, ensure we maintain TranslatableValue structure
      finalValue = newValues.length > 0 ? newValues : [{ text: "", string_id: undefined }];
    } else {
      finalValue = newValues.length > 0 ? newValues : "";
    }
    
    updateFieldValue(fieldId, finalValue);
    // Notify parent so "Save Changes" button gets enabled
    onInputChange(fieldId, finalValue);
  }, [getFieldValue, localFormData, updateFieldValue, onInputChange]);

  /**
   * Move a field instance up or down - ONLY update local state
   */
  const moveFieldInstance = useCallback((fieldId: string, fromIndex: number, direction: 'up' | 'down') => {
    const currentValue = getFieldValue(fieldId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter((v) => v !== undefined);
    
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    
    // Check bounds
    if (toIndex < 0 || toIndex >= currentValues.length) {
      return;
    }
    
    // Swap the items
    const newValues = [...currentValues];
    [newValues[fromIndex], newValues[toIndex]] = [newValues[toIndex], newValues[fromIndex]];
    
    updateFieldValue(fieldId, newValues);
    onInputChange(fieldId, newValues); // Notify parent to persist order
  }, [getFieldValue, updateFieldValue, onInputChange]);

  /**
   * Reorder field instances using drag and drop
   */
  const reorderFieldInstances = useCallback((fieldId: string, fromIndex: number, toIndex: number) => {
    const currentValue = getFieldValue(fieldId);
    const currentValues = Array.isArray(currentValue)
      ? currentValue
      : [currentValue].filter((v) => v !== undefined);
    
    // Check bounds
    if (toIndex < 0 || toIndex >= currentValues.length || fromIndex === toIndex) {
      return;
    }
    
    // Reorder the items
    const newValues = [...currentValues];
    const [movedItem] = newValues.splice(fromIndex, 1);
    newValues.splice(toIndex, 0, movedItem);
    
    updateFieldValue(fieldId, newValues);
    onInputChange(fieldId, newValues); // Notify parent to persist order
  }, [getFieldValue, updateFieldValue, onInputChange]);

  return {
    localFormData,
    setLocalFormData,
    prevSectionsRef,
    getStringContent,
    getCurrentFieldContent,
    updateFieldValue,
    updateTranslatableField,
    getFieldValue,
    handleLocalInputChange,
    handleFieldBlur,
    handleMultipleFieldChange,
    addFieldInstance,
    removeFieldInstance,
    moveFieldInstance,
    reorderFieldInstances,
    cleanupInvalidReferences,
  };
};
